import { ChangeDetectionStrategy, Component, Inject, ViewEncapsulation } from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { toUtcMidnight } from '../../closed-exceptions/helpers/utc-date-helper';
import { getNullActionPlaceholder, setNullActionValue } from '../../extension-details/helpers/null-action-helper';
import { ExtensionDetailView } from '../../extension-details/models/extension-detail-view.model';
import { ExtensionDetailFormService } from '../../extension-details/services/extension-detail-form.service';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { AddTimeBasedRouteFormData } from '../../extension-time-based-routes/models/extension-time-based-routes.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [ExtensionDetailFormService],
    selector: 'ndt-add-time-based-route',
    styleUrls: ['./add-time-based-route.component.scss'],
    templateUrl: './add-time-based-route.component.html',
    standalone: false
})
export class AddTimeBasedRouteComponent implements FormComponent {
   callState: Observable<CallState>;

   form: UntypedFormGroup;

   extension: ExtensionDetailView | null;

   title: string;

   routeExtensionPlaceHolder: string;

   private destroyed$ = new Subject<void>();

   constructor(
      private fb: UntypedFormBuilder,
      private dialogService: LcsDialogService,
      public dialogRef: MatDialogRef<AddTimeBasedRouteComponent>,
      @Inject(MAT_DIALOG_DATA)
      public data: { extension: ExtensionDetailView | null; timeBasedRouteFormData: AddTimeBasedRouteFormData }
   ) {
      const isActiveMonday = data.timeBasedRouteFormData?.isActiveMonday ?? false;
      const isActiveTuesday = data.timeBasedRouteFormData?.isActiveTuesday ?? false;
      const isActiveWednesday = data.timeBasedRouteFormData?.isActiveWednesday ?? false;
      const isActiveThursday = data.timeBasedRouteFormData?.isActiveThursday ?? false;
      const isActiveFriday = data.timeBasedRouteFormData?.isActiveFriday ?? false;
      const isActiveSaturday = data.timeBasedRouteFormData?.isActiveSaturday ?? false;
      const isActiveSunday = data.timeBasedRouteFormData?.isActiveSunday ?? false;
      const isAdd = data.timeBasedRouteFormData == undefined;
      this.form = this.fb.group({
         routeExtensionNullAction: fb.control(
            data.timeBasedRouteFormData?.routeExtensionNullAction ??
               (data.extension?.type?.name === ExtensionTypes.Phone ? NullActionTypes.Voicemail : null)
         ),
         timeBasedRouteId: fb.control(data.timeBasedRouteFormData?.timeBasedRouteId ?? null),
         startDate: fb.control(
            data.timeBasedRouteFormData?.startDate ? this.convertDate(data.timeBasedRouteFormData.startDate) : null
         ),
         startTime: fb.control(
            data.timeBasedRouteFormData?.startTime
               ? this.convertDateToTimeString(data.timeBasedRouteFormData.startTime)
               : null
         ),
         endDate: fb.control(
            data.timeBasedRouteFormData?.endDate ? this.convertDate(data.timeBasedRouteFormData.endDate) : null
         ),
         endTime: fb.control(
            data.timeBasedRouteFormData?.endTime
               ? this.convertDateToTimeString(data.timeBasedRouteFormData.endTime)
               : null
         ),
         routeExtension: fb.control(data.timeBasedRouteFormData?.routeExtension ?? null, Validators.required),
         priorityId: fb.control(data.timeBasedRouteFormData?.priorityId ?? null),
         comment: fb.control(data.timeBasedRouteFormData?.comment ?? null),
         selectAll: fb.control(
            isAdd
               ? true
               : data.timeBasedRouteFormData?.selectAll ??
                    (isActiveMonday &&
                       isActiveTuesday &&
                       isActiveWednesday &&
                       isActiveThursday &&
                       isActiveFriday &&
                       isActiveSaturday &&
                       isActiveSunday)
         ),
         daysOfWeekGroup: fb.group(
            {
               isActiveMonday: fb.control(isAdd || isActiveMonday),
               isActiveTuesday: fb.control(isAdd || isActiveTuesday),
               isActiveWednesday: fb.control(isAdd || isActiveWednesday),
               isActiveThursday: fb.control(isAdd || isActiveThursday),
               isActiveFriday: fb.control(isAdd || isActiveFriday),
               isActiveSaturday: fb.control(isAdd || isActiveSaturday),
               isActiveSunday: fb.control(isAdd || isActiveSunday),
            },
            {
               validators: this.atLeastOneCheckboxRequiredValidator(),
            }
         ),
      });

      this.form.get('startTime')?.valueChanges.subscribe(() => {
         this.updateTimeRangeValidation();
      });
      this.form.get('endTime')?.valueChanges.subscribe(() => {
         this.updateTimeRangeValidation();
      });

      const routeExtensionNullActionControl = this.form.get('routeExtensionNullAction');
      const routeExtensionControl = this.form.get('routeExtension');
      this.routeExtensionPlaceHolder = getNullActionPlaceholder(
         routeExtensionNullActionControl?.value ?? NullActionTypes.Voicemail
      );
      routeExtensionControl?.valueChanges.subscribe((value) => {
         this.routeExtensionPlaceHolder = getNullActionPlaceholder(value);
      });
      let lastRouteValue = { current: null };
      routeExtensionControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value: ExtensionOption | null) => {
            setNullActionValue(value, lastRouteValue, routeExtensionControl, routeExtensionNullActionControl);
            this.routeExtensionPlaceHolder = getNullActionPlaceholder(routeExtensionNullActionControl?.value);
         });

      isAdd ? (this.title = 'Add Inbound Call Rule') : (this.title = 'Edit Inbound Call Rule');
      this.extension = data.extension;
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(): void {
      if (this.form.valid) {
         const formData: AddTimeBasedRouteFormData = this.getAddTimeBasedRouteData(this.form.value);
         this.dialogRef.close(formData);
      }
   }

   private convertDateToTimeString(timeString: Date): string {
      const time = new Date(timeString);
      const hours = time.getUTCHours();
      const minutes = time.getUTCMinutes();
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
   }

   private convertDate(timeString: Date): Date {
      const date = new Date(timeString);
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
   }
   private updateTimeRangeValidation(): void {
      const startTimeControl = this.form.get('startTime');
      const endTimeControl = this.form.get('endTime');

      if (startTimeControl?.value && endTimeControl?.value) {
         const isValidTimeRange = this.isOpenTimeValid();

         if (!isValidTimeRange) {
            startTimeControl?.setErrors({ invalidTimeRange: true });
            endTimeControl?.setErrors({ invalidTimeRange: true });
         } else {
            startTimeControl?.setErrors(null);
            endTimeControl?.setErrors(null);
         }
      }
   }

   private isOpenTimeValid(): boolean {
      const startTime = this.form?.get('startTime')?.value;
      const endTime = this.form?.get('endTime')?.value;
      return startTime && endTime && startTime < endTime;
   }

   private atLeastOneCheckboxRequiredValidator(minRequired = 1): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         let checked = 0;
         let formGroup = control as UntypedFormGroup;

         Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.controls[key];

            if (control.value === true) {
               checked++;
            }
         });
         if (checked < minRequired) {
            return {
               atLeastOneCheckboxRequired: true,
            };
         }
         return null;
      };
   }

   private getAddTimeBasedRouteData(formValue): AddTimeBasedRouteFormData {
      return {
         comment: formValue.comment,
         endDate: toUtcMidnight(formValue.endDate),
         endTime: formValue.endTime,
         isActiveFriday: formValue.daysOfWeekGroup.isActiveFriday,
         isActiveMonday: formValue.daysOfWeekGroup.isActiveMonday,
         isActiveSaturday: formValue.daysOfWeekGroup.isActiveSaturday,
         isActiveSunday: formValue.daysOfWeekGroup.isActiveSunday,
         isActiveThursday: formValue.daysOfWeekGroup.isActiveThursday,
         isActiveTuesday: formValue.daysOfWeekGroup.isActiveTuesday,
         isActiveWednesday: formValue.daysOfWeekGroup.isActiveWednesday,
         priorityId: formValue.priorityId,
         routeExtension: formValue.routeExtension,
         selectAll: formValue.selectAll,
         startDate: toUtcMidnight(formValue.startDate),
         startTime: formValue.startTime,
         timeBasedRouteId: formValue.timeBasedRouteId,
         routeExtensionNullAction: formValue.routeExtensionNullAction,
      };
   }
}
